import { UpdateTradeSettingsDto } from '@core/models';
import { Box, Button, Card, CardActions, CardOverflow, Divider, FormControl, FormLabel, Input, Sheet, Stack, Switch, Typography } from '@mui/joy';
import { getSnapshot } from 'mobx-keystone';
import { observer } from 'mobx-react-lite';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useStore } from 'store/StoreContext';
import { AccountProp } from 'types/account/account';

export const AccountDetails = observer(({ account }: AccountProp) => {
  const { accounts, leaderboard } = useStore();
  const isDataInitiallyFetched = accounts.isInitiallyFetched && leaderboard.isInitiallyFetched;

  return (
    <Stack gap={3}>
      <TradeSettings account={account} />
      {isDataInitiallyFetched && <FollowedTraders account={account} />}
    </Stack>
  );
});

export type FollowedTraders = {
  followedTraders: string[]
}

export const FollowedTraders = observer(({ account }: AccountProp) => {
  const { leaderboard: { followedTraders } } = useStore();
  const {
    register,
    handleSubmit,
    reset,
    formState,
  } = useForm<FollowedTraders>({ defaultValues: { followedTraders: account.followedTraderIds } });

  const onSubmit: SubmitHandler<FollowedTraders> = async data => {
    await account?.updateFollowedTraders({ traders: data.followedTraders });
    reset({ followedTraders: account.followedTraderIds });
  };

  return (
    <Card>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ mb: 1 }}>
          <Typography level="title-md">Followed traders</Typography>
        </Box>
        <Divider />
        <Stack gap={1} paddingY={2}>
          {followedTraders.map(({ id, name }) => (
            <Sheet
              key={id}
              variant="outlined" sx={{
                padding: 2,
                paddingBottom: 1,
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
              }}
            >
              <input type="checkbox" {...register('followedTraders')} value={id} />
              <p>
                {name}
              </p>
            </Sheet>
          ))}
        </Stack>
        <CardOverflow sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
        >
          <CardActions sx={{
            alignSelf: 'flex-end',
            pt: 2,
          }}
          >
            <Button size="sm" variant="solid" disabled={!formState.isDirty} type="submit">
              Save
            </Button>
          </CardActions>
        </CardOverflow>
      </form>
    </Card>
  );
});

export const TradeSettings = observer(({ account }: AccountProp) => {
  const {
    register,
    handleSubmit,
    formState,
    reset,
    control,
  } = useForm<UpdateTradeSettingsDto>({ defaultValues: getSnapshot(account.tradeSettings) });

  const onSubmit: SubmitHandler<UpdateTradeSettingsDto> = async data => {
    await account?.updateTradeSettings(data);
    reset(getSnapshot(account.tradeSettings));
  };

  return (
    <Card>
      <form
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box sx={{ mb: 1 }}>
          <Typography level="title-md">Trade settings</Typography>
        </Box>
        <Divider />
        <Stack direction="row" spacing={4} paddingY={3}>
          <Stack spacing={4}>
            <FormControl>
              <FormLabel>Take profit</FormLabel>
              <Input {...register('tp')} type="number" startDecorator={'%'} />
            </FormControl>
            <FormControl>
              <FormLabel>Stop loss</FormLabel>
              <Input {...register('sl')} type="number" startDecorator={'%'} />
            </FormControl>
          </Stack>
          <Stack spacing={4} >
            <FormControl>
              <FormLabel>Leverage</FormLabel>
              <Input {...register('leverage')} type="number" />
            </FormControl>
            <FormControl>
              <FormLabel>Risk</FormLabel>
              <Input {...register('risk')} type="number" startDecorator={'%'} required />
            </FormControl>
          </Stack>
          <Stack spacing={4} >
            <FormControl>
              <FormLabel>Mirror trader risk</FormLabel>
              <Controller
                name="useTraderRisk"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Switch checked={value} onChange={onChange} />
                )}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Risk / Max risk</FormLabel>
              <Input {...register('risk')} type="number" startDecorator={'%'} required />
            </FormControl>
          </Stack>
        </Stack>
        <CardOverflow sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
        >
          <CardActions sx={{
            alignSelf: 'flex-end',
            pt: 2,
          }}
          >
            <Button size="sm" variant="solid" type="submit" disabled={!formState.isDirty}>
              Save
            </Button>
          </CardActions>
        </CardOverflow>
      </form>
    </Card>
  );
});
